import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import ImageRenderer from "./ImageRenderer";
import { useToast } from "@/hooks/use-toast";

export interface PredictionTarget {
  id: string;
  target_code: string;
  session_id: string;
  event_id: string;
  yes_image_url: string;
  no_image_url: string;
  yes_unsplash_data?: any;
  no_unsplash_data?: any;
  event?: {
    id: string;
    title: string;
    description?: string;
    close_time: string;
    yes_outcome_label: string;
    no_outcome_label: string;
    kalshi_event_id?: string;
    resolved_outcome?: string;
  };
}

interface PredictionRevealRendererProps {
  predictionTarget: PredictionTarget;
  onPredictionSubmit: (prediction: 'YES' | 'NO') => void;
  userPrediction?: 'YES' | 'NO';
  isRevealed?: boolean;
}

const PredictionRevealRenderer: React.FC<PredictionRevealRendererProps> = ({
  predictionTarget,
  onPredictionSubmit,
  userPrediction,
  isRevealed = false,
}) => {
  const [selectedOption, setSelectedOption] = useState<'A' | 'B' | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(isRevealed);
  const { toast } = useToast();

  const handleOptionSelect = (option: 'A' | 'B') => {
    if (hasSubmitted) return; // Don't allow changes after submission
    setSelectedOption(option);
  };

  const handleConfirmSelection = async () => {
    if (!selectedOption || hasSubmitted) return;
    
    setIsSubmitting(true);
    
    // Map A/B to YES/NO (A = YES image, B = NO image)
    const prediction = selectedOption === 'A' ? 'YES' : 'NO';
    
    try {
      // Store the selection as a user message in the database
      // NEW FORMAT: Include target code for unambiguous historic playback
      const { supabase } = await import('@/integrations/supabase/client');
      const { data: { user } } = await supabase.auth.getUser();
      
      const formattedTargetCode = predictionTarget.target_code?.includes('-') 
        ? predictionTarget.target_code 
        : `${predictionTarget.target_code?.substring(0,4)}-${predictionTarget.target_code?.substring(4)}`;
      
      if (user && predictionTarget.session_id) {
        const { error: messageError } = await supabase.from('messages').insert({
          session_id: predictionTarget.session_id,
          content: `My prediction selection for ${formattedTargetCode}: **Option ${selectedOption}**`,
          role: 'user',
          user_id: user.id
        });
        
        if (messageError) {
          console.error('⚠️ Failed to store prediction selection message:', messageError);
        } else {
          console.log('✅ Prediction selection stored in messages table with target code:', formattedTargetCode);
        }
      }
      
      // Submit the prediction - this will trigger the parent to add a new result message
      onPredictionSubmit(prediction);
      setHasSubmitted(true);
      
      toast({
        title: "Prediction Submitted",
        description: "Your selection has been recorded. See below for details!",
      });
    } catch (err) {
      console.error('❌ Error submitting prediction:', err);
      toast({
        title: "Error",
        description: "Failed to submit prediction. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // If already submitted, show a disabled state but keep the selection visible
  const isDisabled = hasSubmitted || !!userPrediction;
  const displaySelection = selectedOption || (userPrediction === 'YES' ? 'A' : userPrediction === 'NO' ? 'B' : null);

  return (
    <div className="space-y-4">
      {/* Image Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Option A (YES Image) */}
        <Card 
          className={`relative overflow-hidden transition-all border-0 bg-card ${
            isDisabled ? 'cursor-default' : 'cursor-pointer'
          } ${
            displaySelection === 'A' 
              ? 'ring-2 ring-primary shadow-lg' 
              : isDisabled 
                ? 'opacity-40 grayscale' 
                : 'hover:ring-1 hover:ring-primary/50'
          }`}
          onClick={() => handleOptionSelect('A')}
        >
          <div className="p-3 pb-2">
            <div className="flex items-center justify-between">
              <span className="font-medium text-foreground">Option A</span>
              {displaySelection === 'A' && (
                <Badge variant="default">{hasSubmitted || userPrediction ? 'Your Selection' : 'Selected'}</Badge>
              )}
            </div>
          </div>
          <CardContent className="p-2 pt-0">
            <ImageRenderer 
              images={[]} 
              imageUrls={[predictionTarget.yes_image_url]}
              unsplashData={predictionTarget.yes_unsplash_data}
              showActionButtons={false}
            />
          </CardContent>
        </Card>

        {/* Option B (NO Image) */}
        <Card 
          className={`relative overflow-hidden transition-all border-0 bg-card ${
            isDisabled ? 'cursor-default' : 'cursor-pointer'
          } ${
            displaySelection === 'B' 
              ? 'ring-2 ring-primary shadow-lg' 
              : isDisabled 
                ? 'opacity-40 grayscale' 
                : 'hover:ring-1 hover:ring-primary/50'
          }`}
          onClick={() => handleOptionSelect('B')}
        >
          <div className="p-3 pb-2">
            <div className="flex items-center justify-between">
              <span className="font-medium text-foreground">Option B</span>
              {displaySelection === 'B' && (
                <Badge variant="default">{hasSubmitted || userPrediction ? 'Your Selection' : 'Selected'}</Badge>
              )}
            </div>
          </div>
          <CardContent className="p-2 pt-0">
            <ImageRenderer 
              images={[]} 
              imageUrls={[predictionTarget.no_image_url]}
              unsplashData={predictionTarget.no_unsplash_data}
              showActionButtons={false}
            />
          </CardContent>
        </Card>
      </div>

      {/* Selection Confirmation - only show if not yet submitted */}
      {!hasSubmitted && !userPrediction && (
        <Card className="bg-card/80 backdrop-blur border-0">
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground mb-4">
              Based on your remote viewing session, which image aligns more closely with your impressions?
            </p>
            <Button 
              className="w-full" 
              onClick={handleConfirmSelection}
              disabled={!selectedOption || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                `Confirm Selection: Option ${selectedOption || '?'}`
              )}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PredictionRevealRenderer;
